'use client'

import { useMemo } from 'react'

import { useQueries } from '@tanstack/react-query'
import axios from 'axios'
import { compact, orderBy, partition } from 'lodash'

import { ADDRESS_ZERO, POPULAR_TOKENS } from '~/constants/addresses'
import { getRpcUrl, SupportedChainId } from '~/constants/chains'
import { QUERY_KEYS } from '~/constants/react-query'
import { REFETCH_BALANCES } from '~/constants/refetch-intervals'
import { useMultipleTokensMetadata } from '~/hooks/useTokenMetadata'
import { useTokenPrices } from '~/hooks/useTokenPrices'
import { EvmAddress } from '~/utils/address'
import { isStablecoin } from '~/utils/addresses'
import { getReadPublicClient } from '~/utils/requests'
import { isNativeTokenAddress } from '~/utils/tokens'

export type WalletTokenBalance = {
  address: EvmAddress.Type
  chainId: SupportedChainId
  amount: bigint
  usdValue?: number
}

// Below this the row renders as "<$0.01", which is not worth depositing.
const MIN_DISPLAYED_USD_VALUE = 0.01

type AlchemyTokenBalancesResponse = {
  result: {
    tokenBalances: Array<{
      contractAddress: string
      tokenBalance: string | null
    }>
  }
}

// The allowlist per chain, minus the native token, which has no contract to
// query and comes from getBalance instead.
const getKnownTokenAddresses = (chainId: SupportedChainId): string[] =>
  (POPULAR_TOKENS[chainId] ?? []).filter(
    (address) => !isNativeTokenAddress(address),
  )

const fetchKnownTokenBalances = async ({
  address,
  chainId,
}: {
  address: EvmAddress.Type
  chainId: SupportedChainId
}): Promise<WalletTokenBalance[]> => {
  const tokenAddresses = getKnownTokenAddresses(chainId)
  if (tokenAddresses.length === 0) return []

  const response = await axios.post<AlchemyTokenBalancesResponse>(
    getRpcUrl(chainId),
    {
      jsonrpc: '2.0',
      id: 1,
      method: 'alchemy_getTokenBalances',
      params: [address, tokenAddresses],
    },
  )

  return (response.data?.result?.tokenBalances ?? [])
    .map((balance) => ({
      address: EvmAddress.format(balance.contractAddress),
      chainId,
      amount: balance.tokenBalance ? BigInt(balance.tokenBalance) : 0n,
    }))
    .filter((balance) => balance.amount > 0n)
}

const fetchWalletBalancesByChain = async ({
  address,
  chainId,
}: {
  address: EvmAddress.Type
  chainId: SupportedChainId
}): Promise<WalletTokenBalance[]> => {
  const [erc20Balances, nativeAmount] = await Promise.all([
    fetchKnownTokenBalances({ address, chainId }),
    getReadPublicClient(chainId).getBalance({ address }),
  ])

  const nativeBalance: WalletTokenBalance | null =
    nativeAmount > 0n
      ? {
          address: EvmAddress.format(ADDRESS_ZERO),
          chainId,
          amount: nativeAmount,
        }
      : null

  return compact([...erc20Balances, nativeBalance])
}

// Unpriced and near-worthless tokens are dropped: the funding check values
// transfers through the same pricing service, so such a deposit would never
// complete the FUND step.
const toDisplayedBalances = (
  balances: WalletTokenBalance[],
): WalletTokenBalance[] => {
  const priced = balances.filter(
    (balance) => (balance.usdValue ?? 0) >= MIN_DISPLAYED_USD_VALUE,
  )
  const [stablecoins, otherTokens] = partition(priced, (balance) =>
    isStablecoin(balance.address, balance.chainId),
  )
  return [
    ...orderBy(stablecoins, (balance) => balance.usdValue, 'desc'),
    ...orderBy(otherTokens, (balance) => balance.usdValue, 'desc'),
  ]
}

// The connected wallet's balances on the treasury chains, limited to the
// tokens we know (POPULAR_TOKENS plus the native token). An allowlist rather
// than a filter: spam never reaches the client, so no heuristic has to catch
// it. Stablecoins rank first.
export const useWalletDepositBalances = ({
  address,
  chainIds,
}: {
  address: EvmAddress.Type
  chainIds: SupportedChainId[]
}) => {
  const { convertTokenToUSD, hasPendingPricingCalls } = useTokenPrices()

  const balanceQueries = useQueries({
    queries: chainIds.map((chainId) => ({
      queryKey: [QUERY_KEYS.WALLET_DEPOSIT_BALANCES, address, chainId],
      queryFn: () => fetchWalletBalancesByChain({ address, chainId }),
      refetchInterval: REFETCH_BALANCES,
    })),
  })

  const isLoadingQueries = balanceQueries.some((query) => query.isLoading)

  const rawBalances = useMemo(
    () => balanceQueries.flatMap((query) => query.data ?? []),
    [balanceQueries],
  )

  useMultipleTokensMetadata(
    rawBalances.map((balance) => ({
      address: balance.address,
      chainId: balance.chainId,
    })),
    { includePrice: true },
  )

  const balances = useMemo<WalletTokenBalance[]>(
    () =>
      toDisplayedBalances(
        rawBalances.map((balance) => ({
          ...balance,
          usdValue:
            convertTokenToUSD({
              token: balance.address,
              amount: balance.amount,
              chainId: balance.chainId,
            }) ?? undefined,
        })),
      ),
    [convertTokenToUSD, rawBalances],
  )

  return {
    balances,
    isLoading: isLoadingQueries || hasPendingPricingCalls(),
  }
}
